import type { AdminExpertDetail, AdminSetting } from '@shopexperts/legacy-api';

const CLAIM_LINK_TEXT_KEY = 'admin_emails.unclaimed_claim_link_text';
// The setting may be blank or absent until the API's admin-settings:sync has run.
const DEFAULT_CLAIM_LINK_TEXT = 'claim your free listing';

export function inviteTemplateKeys(listingType: string) {
  const prefix = `admin_emails.unclaimed_${listingType === 'freelancer' ? 'freelancer' : 'agency'}`;
  return { subject: `${prefix}_subject`, body: `${prefix}_body` };
}

export function inviteTemplateField(key: string): 'subject' | 'body' | undefined {
  for (const type of ['agency', 'freelancer']) {
    const keys = inviteTemplateKeys(type);
    if (key === keys.subject) return 'subject';
    if (key === keys.body) return 'body';
  }
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export function renderInviteTemplate(
  settings: AdminSetting[],
  expert: Pick<AdminExpertDetail, 'display_name' | 'listing_type'>,
  profileUrl: string,
): { subject: string; body: string } {
  const keys = inviteTemplateKeys(expert.listing_type);
  const subject = settings.find((setting) => setting.key === keys.subject)?.value;
  const body = settings.find((setting) => setting.key === keys.body)?.value;
  const configuredLinkText = settings.find((setting) => setting.key === CLAIM_LINK_TEXT_KEY)?.value;
  const linkText = typeof configuredLinkText === 'string' && configuredLinkText.trim()
    ? configuredLinkText.trim()
    : DEFAULT_CLAIM_LINK_TEXT;
  if (typeof subject !== 'string' || typeof body !== 'string') {
    throw new Error('Invitation settings are missing. Check Admin settings, then retry.');
  }
  if (body.includes('{{claim_link}}') && !profileUrl) {
    throw new Error('This expert needs a profile URL before a claim link can be generated.');
  }
  const names = {
    display_name: expert.display_name,
    first_name: expert.listing_type === 'freelancer' ? expert.display_name.trim().split(/\s+/)[0] || 'there' : 'there',
  };
  const html = {
    display_name: escapeHtml(names.display_name),
    first_name: escapeHtml(names.first_name),
    claim_link: `<a href="${escapeHtml(profileUrl)}">${escapeHtml(linkText)}</a>`,
  };
  // Replace once so values containing placeholder-like text are never reinterpreted.
  return {
    subject: subject.replace(/{{(display_name|first_name)}}/g, (_, key: keyof typeof names) => names[key]),
    body: body.replace(/{{(display_name|first_name|claim_link)}}/g, (_, key: keyof typeof html) => html[key]),
  };
}
